<!-- src/components/auth/EnrolementMfa.vue -->
<!--
  L'enrôlement du second facteur, en deux volets : le choix de la méthode, puis les codes de
  secours.

  Ce composant sert TROIS parcours qui aboutissent au même geste — la première connexion d'un
  client, l'acceptation d'une invitation d'administrateur, et la reconfiguration depuis le
  profil après une réinitialisation. Les écrire trois fois aurait garanti que le deuxième
  volet, celui des codes de secours, finisse par être bâclé quelque part.

  Deux méthodes sont possibles, et c'est le SERVEUR qui dit lesquelles : application
  d'authentification pour tout le monde, code par courriel pour les clients seulement. Les
  administrateurs restent en TOTP strict — ce sont eux la cible. Imposer une application à
  tous les clients, en revanche, aurait enfermé dehors ceux qui n'ont pas de smartphone, sur
  un produit où ne pas pouvoir se connecter est un problème d'exploitation.

  La différence entre les parcours tient à la preuve apportée : en cours de connexion c'est le
  jeton de passage (`mfaToken`) ; depuis le profil c'est la session, et l'intercepteur axios
  pose l'en-tête tout seul.
-->
<template>
  <div class="space-y-6">

    <!-- ── Volet 2 : les codes de secours ──────────────────────────────────────────────── -->
    <!-- Affiché APRÈS la confirmation. C'est le seul et unique moment où ces codes existent
         en clair : le serveur n'en garde que des empreintes. -->
    <div v-if="codes.length" class="space-y-4">
      <div class="rounded-md bg-amber-50 border border-amber-200 p-4">
        <h3 class="text-sm font-semibold text-amber-900">{{ $t('enrolement.codesTitre') }}</h3>
        <i18n-t keypath="enrolement.codesAvertissement" tag="p" class="mt-1 text-sm text-amber-800">
          <template #jamais><strong>{{ $t('enrolement.codesJamais') }}</strong></template>
        </i18n-t>
      </div>

      <div class="grid grid-cols-2 gap-2 rounded-md border border-gray-200 bg-gray-50 p-4 font-mono text-sm">
        <div v-for="c in codes" :key="c" class="tracking-wider text-gray-800">{{ c }}</div>
      </div>

      <div class="flex flex-wrap gap-2">
        <button type="button" @click="copier"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          {{ copie ? $t('enrolement.copies') : $t('enrolement.copier') }}
        </button>
        <button type="button" @click="telecharger"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          {{ $t('enrolement.telecharger') }}
        </button>
        <button type="button" @click="imprimer"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          {{ $t('enrolement.imprimer') }}
        </button>
      </div>

      <!-- La case à cocher n'est pas une formalité : sans elle, on clique « Continuer » par
           réflexe et on découvre trois mois plus tard qu'on n'a jamais noté les codes. -->
      <label class="flex items-start gap-2 text-sm text-gray-700">
        <input type="checkbox" v-model="codesNotes" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600">
        <span>{{ $t('enrolement.codesNotes') }}</span>
      </label>

      <button type="button" @click="terminer" :disabled="!codesNotes"
              class="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed">
        {{ $t('enrolement.continuer') }}
      </button>
    </div>

    <!-- ── Volet 1 : configurer le second facteur ──────────────────────────────────────── -->
    <div v-else class="space-y-5">
      <div>
        <h3 class="text-base font-semibold text-gray-900">{{ $t('enrolement.titre') }}</h3>
        <p class="mt-1 text-sm text-gray-600">
          {{ $t('enrolement.intro') }}
        </p>
      </div>

      <!-- Le choix n'apparaît que s'il y en a un : un administrateur ne voit rien à choisir,
           et un écran qui propose une seule option est un écran qui fait perdre du temps. -->
      <div v-if="methodesPossibles.length > 1" class="grid gap-3 sm:grid-cols-2">
        <button type="button" @click="choisir('totp')"
                :class="['rounded-lg border p-3 text-left', methode === 'totp'
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-300 hover:bg-gray-50']">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-gray-900">{{ $t('enrolement.appli') }}</span>
            <span class="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">{{ $t('enrolement.recommande') }}</span>
          </div>
          <p class="mt-1 text-xs text-gray-600">
            {{ $t('enrolement.appliAide') }}
          </p>
        </button>
        <button type="button" @click="choisir('email')"
                :class="['rounded-lg border p-3 text-left', methode === 'email'
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-300 hover:bg-gray-50']">
          <span class="text-sm font-medium text-gray-900">{{ $t('enrolement.courriel') }}</span>
          <p class="mt-1 text-xs text-gray-600">
            {{ $t('enrolement.courrielAide') }}
          </p>
        </button>
      </div>

      <div v-if="erreur" class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        {{ erreur }}
      </div>

      <div v-if="chargement" class="py-10 text-center text-sm text-gray-500">{{ $t('enrolement.preparation') }}</div>

      <!-- ── Méthode : application d'authentification ───────────────────────────────────── -->
      <template v-else-if="methode === 'totp' && qr">
        <ol class="space-y-4 text-sm text-gray-700">
          <li>
            <span class="font-medium">1.</span> <i18n-t keypath="enrolement.etape1"><template #appli><strong>Google Authenticator</strong></template></i18n-t>
            <!-- Le premier utilisateur de cet écran s'y est fait prendre : il a scanné avec
                 l'appareil photo, Android a confié le code à Samsung Pass, qui a créé une
                 fiche de mot de passe inutilisable. La confusion entre « gestionnaire de mots
                 de passe » et « application d'authentification » est le piège n°1 de cette
                 page — elle mérite d'être désamorcée ici plutôt qu'au téléphone. -->
            <p class="mt-1 text-xs text-gray-500">
              {{ $t('enrolement.etape1Attention') }}
            </p>
          </li>
          <li>
            <span class="font-medium">2.</span> <i18n-t keypath="enrolement.etape2"><template #ouvrez><strong>{{ $t('enrolement.etape2Ouvrez') }}</strong></template></i18n-t>
            <p class="mt-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {{ $t('enrolement.etape2Camera') }}
            </p>
            <div class="mt-2 flex justify-center">
              <img :src="qr" :alt="$t('enrolement.qrAlt')" class="rounded-md border border-gray-200 bg-white p-2">
            </div>
            <!-- Beaucoup de gestionnaires de mots de passe de bureau n'ont pas d'appareil
                 photo : sans saisie manuelle, ils sont exclus. -->
            <p class="mt-2 text-center text-xs text-gray-500">
              {{ $t('enrolement.pasDeCamera') }}
              <button type="button" @click="montrerSecret = !montrerSecret" class="font-medium text-primary-600 hover:text-primary-500">
                {{ $t('enrolement.saisirCle') }}
              </button>
            </p>
            <div v-if="montrerSecret" class="mt-2 break-all rounded-md bg-gray-50 border border-gray-200 p-3 text-center font-mono text-sm tracking-wider text-gray-800">
              {{ secret }}
            </div>
          </li>
          <li>
            <span class="font-medium">3.</span> {{ $t('enrolement.etape3') }}
            <input v-model="code" inputmode="numeric" autocomplete="one-time-code" maxlength="7"
                   placeholder="123 456" @keyup.enter="confirmer"
                   class="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-center font-mono text-xl tracking-[0.3em] focus:border-primary-500 focus:ring-primary-500">
          </li>
        </ol>
      </template>

      <!-- ── Méthode : code par courriel ────────────────────────────────────────────────── -->
      <template v-else-if="methode === 'email'">
        <div class="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          <i18n-t v-if="envoi && envoi.envoye" keypath="enrolement.codeEnvoye" tag="p">
            <template #adresse><strong>{{ envoi.adresseMasquee }}</strong></template>
            <template #minutes>{{ envoi.validiteMin }}</template>
          </i18n-t>
          <p v-else class="text-amber-800">
            {{ $t('enrolement.courrielEchec') }}
          </p>
          <p class="mt-2 text-xs text-gray-500">
            {{ $t('enrolement.courrielPreuve') }}
          </p>
        </div>

        <input v-model="code" inputmode="numeric" autocomplete="one-time-code" maxlength="7"
               placeholder="123 456" @keyup.enter="confirmer"
               class="block w-full rounded-md border border-gray-300 px-3 py-3 text-center font-mono text-2xl tracking-[0.3em] focus:border-primary-500 focus:ring-primary-500">

        <p class="text-center text-sm">
          <button type="button" @click="renvoyer" :disabled="attenteRenvoi > 0 || envoiEnCours"
                  class="font-medium text-primary-600 hover:text-primary-500 disabled:text-gray-400">
            {{ attenteRenvoi > 0 ? $t('enrolement.renvoyerDans', { s: attenteRenvoi }) : $t('enrolement.renvoyer') }}
          </button>
        </p>
      </template>

      <label v-if="proposerMemorisation && !chargement" class="flex items-start gap-2 text-sm text-gray-700">
        <input type="checkbox" v-model="memoriser" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600">
        <span>
          {{ $t('enrolement.memoriser') }}
          <span class="block text-xs text-gray-500">
            {{ $t('enrolement.memoriserAide') }}
          </span>
        </span>
      </label>

      <button v-if="!chargement" type="button" @click="confirmer"
              :disabled="envoiEnCours || code.replace(/\D/g, '').length !== 6"
              class="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed">
        {{ envoiEnCours ? $t('enrolement.verification') : $t('enrolement.activer') }}
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import apiService from '@/services/api.service';
import { useI18n } from 'vue-i18n';
import { texteErreur } from '@/i18n/serveur';

export default defineComponent({
  name: 'EnrolementMfa',

  props: {
    // Jeton de passage, quand l'enrôlement a lieu pendant une connexion. Absent quand on
    // arrive depuis le profil : la session suffit alors.
    mfaToken: { type: String, default: null },
    // La mémorisation d'appareil n'a de sens que pendant une connexion.
    proposerMemorisation: { type: Boolean, default: true }
  },

  // `termine` porte la réponse du serveur : elle contient le jeton d'accès quand
  // l'enrôlement s'est fait en cours de connexion, et rien de plus quand il vient du profil.
  emits: ['termine'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const methode = ref('totp'), methodesPossibles = ref(['totp']);
    const qr = ref(null), secret = ref(''), code = ref(''), envoi = ref(null);
    const codes = ref([]);
    const chargement = ref(true), envoiEnCours = ref(false), erreur = ref('');
    const montrerSecret = ref(false), memoriser = ref(false);
    const codesNotes = ref(false), copie = ref(false);
    const attenteRenvoi = ref(0);
    let reponseFinale = null, minuteur = null;

    const corps = (extra = {}) => (props.mfaToken ? { mfaToken: props.mfaToken, ...extra } : extra);

    // Décompte avant de pouvoir redemander un code. Purement cosmétique — le serveur refuse
    // de toute façon — mais un bouton qui échoue sans explication est pire qu'un bouton grisé.
    const lancerDecompte = (s) => {
      attenteRenvoi.value = s;
      clearInterval(minuteur);
      minuteur = setInterval(() => {
        if (--attenteRenvoi.value <= 0) clearInterval(minuteur);
      }, 1000);
    };

    const demarrer = async (methodeVoulue) => {
      chargement.value = true; erreur.value = ''; code.value = ''; qr.value = null; envoi.value = null;
      try {
        const { data } = await apiService.mfaEnrolementDemarrer(corps(
          methodeVoulue ? { methode: methodeVoulue } : {}
        ));
        // 🔴 C'est la réponse du SERVEUR qui fait foi, pas ce qu'on a demandé : un
        // administrateur qui réclamerait le courriel se voit rendre du TOTP, et l'écran doit
        // afficher ce qui a réellement été mis en place.
        methode.value = data.methode;
        methodesPossibles.value = data.methodesPossibles || [data.methode];
        qr.value = data.qr || null;
        secret.value = data.secret || '';
        envoi.value = data.envoi || null;
        if (data.envoi?.envoye) lancerDecompte(data.envoi.renvoiDansS || 60);
      } catch (e) {
        erreur.value = texteErreur(e, t('enrolement.erreurPreparation'));
      } finally {
        chargement.value = false;
      }
    };

    const choisir = (m) => { if (m !== methode.value) demarrer(m); };

    const renvoyer = async () => {
      envoiEnCours.value = true; erreur.value = '';
      try {
        const { data } = await apiService.mfaRenvoyerCode(props.mfaToken, 'enroll');
        envoi.value = data.envoi;
        lancerDecompte(data.envoi?.renvoiDansS || 60);
      } catch (e) {
        if (e.response?.data?.code === 'TROP_TOT') lancerDecompte(e.response.data.attente_s || 60);
        erreur.value = texteErreur(e, t('enrolement.renvoiImpossible'));
      } finally {
        envoiEnCours.value = false;
      }
    };

    const confirmer = async () => {
      const propre = code.value.replace(/\D/g, '');
      if (propre.length !== 6 || envoiEnCours.value) return;
      envoiEnCours.value = true; erreur.value = '';
      try {
        const { data } = await apiService.mfaEnrolementConfirmer(
          corps({ code: propre, rememberDevice: memoriser.value })
        );
        codes.value = data.backupCodes || [];
        reponseFinale = data;
        // Aucun code de secours rendu (cas qui ne devrait pas arriver) : ne pas bloquer
        // l'utilisateur devant un écran vide.
        if (!codes.value.length) emit('termine', data);
      } catch (e) {
        erreur.value = texteErreur(e, t('enrolement.codeIncorrect'));
        code.value = '';
      } finally {
        envoiEnCours.value = false;
      }
    };

    const texteCodes = () => [
      t('enrolement.fichierTitre'),
      t('enrolement.fichierUneFois'),
      t('enrolement.fichierLieuSur'),
      '', ...codes.value.map(c => `    ${c}`), ''
    ].join('\n');

    const copier = async () => {
      try {
        await navigator.clipboard.writeText(codes.value.join('\n'));
        copie.value = true;
        setTimeout(() => { copie.value = false; }, 2500);
      } catch (e) { erreur.value = t('enrolement.copieImpossible'); }
    };

    const telecharger = () => {
      const url = URL.createObjectURL(new Blob([texteCodes()], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url; a.download = t('enrolement.nomFichier');
      a.click();
      URL.revokeObjectURL(url);
    };

    const imprimer = () => {
      const f = window.open('', '_blank');
      if (!f) { erreur.value = t('enrolement.impressionBloquee'); return; }
      f.document.write(`<pre style="font:14px/1.9 monospace">${texteCodes()}</pre>`);
      f.document.close();
      f.print();
    };

    const terminer = () => emit('termine', reponseFinale);

    onMounted(() => demarrer(null));
    onUnmounted(() => clearInterval(minuteur));

    return { methode, methodesPossibles, qr, secret, code, codes, envoi, attenteRenvoi,
             chargement, envoiEnCours, erreur, montrerSecret, memoriser, codesNotes, copie,
             choisir, renvoyer, confirmer, copier, telecharger, imprimer, terminer };
  }
});
</script>
